import type { BillingBudget, BillingBudgetField, BillingMonthlyBudgetField } from '../types/billing';
import { BILLING_MONTHLY_BUDGET_FIELDS, BILLING_MONTHLY_SPEND_FIELDS } from '../types/billing';
import { MONTH_DISPLAY_LABELS, type MonthPrefix } from './billingTableConfig';
import { billingValuesEqual } from './billingHelpers';

export type BillingBudgetValidationErrorCode =
  | 'missing_yearly_limit'
  | 'negative_month_budget'
  | 'exceeds_yearly_limit'
  | 'invalid_month_budget'
  | 'past_month_edit_locked'
  | 'insufficient_savings';

export interface BillingBudgetValidationError {
  code: BillingBudgetValidationErrorCode;
  message: string;
}

export type MonthlyBudgetChangeResult =
  | {
      ok: true;
      row: BillingBudget;
      adjustedFutureMonths: BillingMonthlyBudgetField[];
      revisedRedistribution: boolean;
    }
  | { ok: false; error: BillingBudgetValidationError };

function monthPrefixFromBudgetField(field: BillingMonthlyBudgetField): MonthPrefix {
  return field.replace('_budget', '') as MonthPrefix;
}

function monthLabel(field: BillingMonthlyBudgetField): string {
  return MONTH_DISPLAY_LABELS[monthPrefixFromBudgetField(field)];
}

function formatAmount(n: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function monthBudgetAmount(row: BillingBudget, field: BillingMonthlyBudgetField): number {
  const v = row[field];
  if (v === null || v === undefined || Number.isNaN(Number(v))) {
    return 0;
  }
  return Number(v);
}

function monthFieldAmount(row: BillingBudget, field: BillingBudgetField): number {
  const v = row[field];
  if (v === null || v === undefined || Number.isNaN(Number(v))) {
    return 0;
  }
  return Number(v);
}

/**
 * Cumulative net overspend (actual spend − budget) from Jan through the current
 * calendar month inclusive, floored at 0. Underspent months offset overspent ones
 * (cumulative net), matching the sandbox-billing-manager updation step that reduces
 * subsequent months' budgets by the overspend.
 *
 * This is the amount that must be carved out of the yearly budget, so the invariant
 * holds: yearly budget − Σ(all monthly budgets) === overspend until current month.
 */
export function getOverspendUntilCurrentMonth(
  row: BillingBudget,
  calendarMonthIndex = getCalendarMonthBudgetIndex(),
): number {
  const lastIndex = Math.min(calendarMonthIndex, BILLING_MONTHLY_BUDGET_FIELDS.length - 1);
  let spendSum = 0;
  let budgetSum = 0;
  for (let i = 0; i <= lastIndex; i++) {
    spendSum += monthFieldAmount(row, BILLING_MONTHLY_SPEND_FIELDS[i]);
    budgetSum += monthBudgetAmount(row, BILLING_MONTHLY_BUDGET_FIELDS[i]);
  }
  return Math.max(0, Math.round((spendSum - budgetSum) * 100) / 100);
}

/**
 * Cumulative net savings (budget − actual spend) from Jan through the current calendar
 * month inclusive, floored at 0. The mirror image of getOverspendUntilCurrentMonth: this
 * is unused budget from months already committed, available to cover a direct increase
 * to a current/future month's budget without touching the annual budget.
 */
export function getSavingsUntilCurrentMonth(
  row: BillingBudget,
  calendarMonthIndex = getCalendarMonthBudgetIndex(),
): number {
  const lastIndex = Math.min(calendarMonthIndex, BILLING_MONTHLY_BUDGET_FIELDS.length - 1);
  let spendSum = 0;
  let budgetSum = 0;
  for (let i = 0; i <= lastIndex; i++) {
    spendSum += monthFieldAmount(row, BILLING_MONTHLY_SPEND_FIELDS[i]);
    budgetSum += monthBudgetAmount(row, BILLING_MONTHLY_BUDGET_FIELDS[i]);
  }
  return Math.max(0, Math.round((budgetSum - spendSum) * 100) / 100);
}

/** 0-based index into BILLING_MONTHLY_BUDGET_FIELDS (Jan=0 … Dec=11). */
export function getCalendarMonthBudgetIndex(referenceDate = new Date()): number {
  return referenceDate.getMonth();
}

export function getYearlyBudgetLimit(row: BillingBudget): number | null {
  if (row.annual_fixed_budget !== null && row.annual_fixed_budget !== undefined) {
    return row.annual_fixed_budget;
  }
  return null;
}

function distributeRemainingAcrossMonths(
  remaining: number,
  fields: BillingMonthlyBudgetField[],
): Record<BillingMonthlyBudgetField, number> {
  const result = {} as Record<BillingMonthlyBudgetField, number>;
  const count = fields.length;
  if (count === 0) {
    return result;
  }
  const perMonth = Math.round((remaining / count) * 100) / 100;
  let assigned = 0;
  for (let i = 0; i < count - 1; i++) {
    result[fields[i]] = perMonth;
    assigned += perMonth;
  }
  const lastField = fields[count - 1];
  result[lastField] = Math.round((remaining - assigned) * 100) / 100;
  return result;
}

/**
 * Redistribution triggered by editing the annual budget itself: past months are left
 * untouched, and the annual budget minus (prior months' budgets + year-to-date overspend)
 * is split evenly across the current month → December.
 */
export function applyAnnualBudgetRedistribution(
  row: BillingBudget,
  calendarMonthIndex: number,
): MonthlyBudgetChangeResult {
  const yearlyLimit = getYearlyBudgetLimit(row)!;

  let priorSum = 0;
  for (let i = 0; i < calendarMonthIndex; i++) {
    priorSum += monthBudgetAmount(row, BILLING_MONTHLY_BUDGET_FIELDS[i]);
  }

  const overspend = getOverspendUntilCurrentMonth(row, calendarMonthIndex);
  const remaining = Math.round((yearlyLimit - overspend - priorSum) * 100) / 100;
  if (remaining < 0) {
    const overspendNote =
      overspend > 0 ? ` ($${formatAmount(overspend)} reserved for year-to-date overspend)` : '';
    return {
      ok: false,
      error: {
        code: 'exceeds_yearly_limit',
        message: `Annual budget of $${formatAmount(yearlyLimit)} is insufficient to cover prior months' budgets${overspendNote}.`,
      },
    };
  }

  const targetFields = BILLING_MONTHLY_BUDGET_FIELDS.slice(calendarMonthIndex);
  const amounts = distributeRemainingAcrossMonths(remaining, targetFields);

  const updated: BillingBudget = { ...row };
  for (const field of targetFields) {
    const amount = amounts[field];
    if (amount < 0) {
      return {
        ok: false,
        error: {
          code: 'exceeds_yearly_limit',
          message: 'Annual budget leaves insufficient budget for the current and following months.',
        },
      };
    }
    updated[field] = amount;
  }

  return {
    ok: true,
    row: updated,
    adjustedFutureMonths: targetFields.filter((f) => !billingValuesEqual(row[f], updated[f])),
    revisedRedistribution: true,
  };
}

/**
 * Direct edit of a single month's budget, without changing the annual budget. No other
 * month is touched. An increase must be covered by year-to-date net savings (unused
 * budget from months already committed) — if there isn't enough, the user is told to
 * increase the annual budget instead. A decrease is always allowed.
 */
export function applyManualMonthlyBudgetChange(
  row: BillingBudget,
  changedField: BillingMonthlyBudgetField,
  newValue: number | null,
  options?: { calendarMonthIndex?: number },
): MonthlyBudgetChangeResult {
  const yearlyLimit = getYearlyBudgetLimit(row);
  if (yearlyLimit === null) {
    return {
      ok: false,
      error: {
        code: 'missing_yearly_limit',
        message: 'Set an annual budget before editing monthly budgets.',
      },
    };
  }

  if (newValue === null || Number.isNaN(newValue)) {
    return {
      ok: false,
      error: {
        code: 'invalid_month_budget',
        message: `${monthLabel(changedField)} budget must be a valid number.`,
      },
    };
  }

  if (newValue < 0) {
    return {
      ok: false,
      error: {
        code: 'negative_month_budget',
        message: 'Monthly budget cannot be negative.',
      },
    };
  }

  const changedIndex = BILLING_MONTHLY_BUDGET_FIELDS.indexOf(changedField);
  if (changedIndex < 0) {
    return {
      ok: false,
      error: {
        code: 'invalid_month_budget',
        message: 'Invalid monthly budget field.',
      },
    };
  }

  const calendarMonthIndex = options?.calendarMonthIndex ?? getCalendarMonthBudgetIndex();

  if (changedIndex < calendarMonthIndex) {
    return {
      ok: false,
      error: {
        code: 'past_month_edit_locked',
        message: 'Cannot edit past months.',
      },
    };
  }

  const oldValue = monthBudgetAmount(row, changedField);
  const delta = Math.round((newValue - oldValue) * 100) / 100;
  if (delta > 0) {
    const savings = getSavingsUntilCurrentMonth(row, calendarMonthIndex);
    if (delta > savings + 0.005) {
      return {
        ok: false,
        error: {
          code: 'insufficient_savings',
          message: `Only $${formatAmount(savings)} in year-to-date savings is available; increase the annual budget to add more to ${monthLabel(changedField)}.`,
        },
      };
    }
  }

  return {
    ok: true,
    row: { ...row, [changedField]: newValue },
    adjustedFutureMonths: [],
    revisedRedistribution: false,
  };
}

function changedMonthlyBudgetFields(
  dbRow: BillingBudget,
  editedRow: BillingBudget,
): BillingMonthlyBudgetField[] {
  return BILLING_MONTHLY_BUDGET_FIELDS.filter(
    (field) => !billingValuesEqual(dbRow[field], editedRow[field]),
  );
}

export function normalizeMonthlyBudgetEdits(
  dbRow: BillingBudget,
  editedRow: BillingBudget,
  options?: { calendarMonthIndex?: number },
): MonthlyBudgetChangeResult {
  const changedFields = changedMonthlyBudgetFields(dbRow, editedRow);
  const annualBudgetChanged = !billingValuesEqual(
    dbRow.annual_fixed_budget,
    editedRow.annual_fixed_budget,
  );
  if (changedFields.length === 0 && !annualBudgetChanged) {
    return {
      ok: true,
      row: editedRow,
      adjustedFutureMonths: [],
      revisedRedistribution: false,
    };
  }

  const calendarMonthIndex =
    options?.calendarMonthIndex ?? getCalendarMonthBudgetIndex();

  // Any changed month before the current calendar month is always rejected, whether or
  // not the annual budget also changed.
  for (const field of changedFields) {
    const index = BILLING_MONTHLY_BUDGET_FIELDS.indexOf(field);
    if (index < calendarMonthIndex) {
      return {
        ok: false,
        error: {
          code: 'past_month_edit_locked',
          message: 'Cannot edit past months.',
        },
      };
    }
  }

  // Annual-budget path: triggered by an annual_fixed_budget change. Past months stay
  // untouched; the current month → December are recomputed as one block.
  if (annualBudgetChanged) {
    return applyAnnualBudgetRedistribution({ ...editedRow }, calendarMonthIndex);
  }

  if (changedFields.length === 0) {
    return {
      ok: true,
      row: editedRow,
      adjustedFutureMonths: [],
      revisedRedistribution: false,
    };
  }

  // Monthly-cell-edit path: no other months are touched. Any net increase across the
  // changed months must be covered by year-to-date savings from the (unedited) db row.
  let totalIncrease = 0;
  for (const field of changedFields) {
    const oldValue = monthBudgetAmount(dbRow, field);
    const newValue = monthBudgetAmount(editedRow, field);
    const delta = Math.round((newValue - oldValue) * 100) / 100;
    if (delta > 0) totalIncrease += delta;
  }

  if (totalIncrease > 0) {
    const savings = getSavingsUntilCurrentMonth(dbRow, calendarMonthIndex);
    if (totalIncrease > savings + 0.005) {
      return {
        ok: false,
        error: {
          code: 'insufficient_savings',
          message: `Only $${formatAmount(savings)} in year-to-date savings is available; increase the annual budget first.`,
        },
      };
    }
  }

  return {
    ok: true,
    row: editedRow,
    adjustedFutureMonths: [],
    revisedRedistribution: false,
  };
}

export function describeAdjustedMonths(
  fields: BillingMonthlyBudgetField[],
  revisedRedistribution = false,
): string | null {
  if (fields.length === 0) return null;
  const labels = fields.map((f) => monthLabel(f));
  if (revisedRedistribution) {
    if (labels.length === 1) {
      return `Redistributed ${labels[0]} budget equally based on the updated annual budget.`;
    }
    return `Redistributed ${labels[0]}–${labels[labels.length - 1]} budgets equally based on the updated annual budget.`;
  }
  if (labels.length === 1) {
    return `Adjusted ${labels[0]} budget to stay within the yearly limit.`;
  }
  return `Adjusted ${labels[0]}–${labels[labels.length - 1]} budgets to stay within the yearly limit.`;
}
